// What every structure symbol set shares (R1103, R1104, mockup round 054).
//
// The kinds, the id roster, the ink one mark draws in, and the small path
// helpers the art of a set is written with. One file, so a new art lane adds
// `<id>.ts` beside `default.ts` and imports its geometry from here rather
// than copying it.
//
// The art contract (`marks.ts`): `MarkGraphics` carries `moveTo`, `lineTo`,
// `arc`, `closePath`, `poly`, `circle`, `fill` and `stroke`, and nothing
// else. There is no quadratic curve, no rounded rectangle, no dash array, no
// clip, no text and no transform. A curve is sampled into a point list here,
// a rotation is applied to the points, and a dashed ring is one arc per dash.
// A mark draws at the origin, and the caller places it.

import type { MarkGraphics } from '../marks';
// The two curve samplers are the mark kit's own; a set file reads them here
// beside the helpers of this lane.
export { cubic, quadratic } from '../marks';
import { ObjKind } from '../../gen/proto/aquaphobia';

/**
 * The nine kinds of strategic object, in the words of the wire (numbers.md
 * §8). `OBJ_KIND_FORGE` is `forge`, `OBJ_KIND_KELP` is `kelp`,
 * `OBJ_KIND_DRYDOCK` is `drydock`, `OBJ_KIND_ARCHIVE` is `archive` and
 * `OBJ_KIND_MARSHAL` is `marshal`.
 */
export const STRUCTURE_KINDS = [
  'throne', 'spire', 'forge', 'kelp', 'battery',
  'drydock', 'archive', 'marshal', 'ward',
] as const;

export type StructureKind = typeof STRUCTURE_KINDS[number];

/**
 * The arcade structures that are no strategic object of the season map
 * (R2085): the Reef Breakwater. No set draws one; every set draws the one
 * mark `DEFAULT_BREAKWATER` of `default.ts`.
 */
export const ARCADE_MARK_KINDS = ['breakwater'] as const;

/** Every kind a structure mark draws: the nine of a set and the arcade ones. */
export type MarkKind = StructureKind | typeof ARCADE_MARK_KINDS[number];

/**
 * The ten sets a structure symbol can wear, in the cycle order of the field.
 * The first is the default.
 *
 * `data/identity/labels.toml` states the same list, and it reaches the client
 * as `STRUCTURE_SYMBOLS` of `gen/balance.ts`. This module holds no generated
 * import, because the atlas and the menu previews draw marks with no balance
 * pack; a test pins the two lists equal instead, the way `HOLD_SYMBOL_IDS`
 * is pinned (R633).
 */
export const STRUCTURE_SYMBOL_IDS = [
  'default', 'pictogram', 'heraldic', 'silhouette', 'blocks',
  'echo', 'gauge-ring', 'tile', 'letters', 'buoys',
] as const;

export type StructureSymbolId = typeof STRUCTURE_SYMBOL_IDS[number];

/**
 * The ink one structure mark draws in.
 *
 * `own` is the holder's colour, or the neutral grey while nobody holds the
 * object. `foe` is the colour of the faction that pledged an attack, and it
 * is 0 while no pledge stands: a mark draws its contested tell only when
 * `foe` is not 0. `held` says whether the object has a holder, which is what
 * makes a held mark brighter than a neutral one.
 */
export interface StructureInk {
  own: number;
  foe: number;
  held: boolean;
}

/** One kind of one set. It draws at the origin and paints its own ink. */
export type StructureDraw = (g: MarkGraphics, ink: StructureInk) => void;

/** One whole set: all nine kinds. */
export type StructureSet = Record<StructureKind, StructureDraw>;

/** One whole set from one `markOf`: every kind of the roster, in its order. */
export function setOf(markOf: (kind: StructureKind) => StructureDraw): StructureSet {
  const out = {} as StructureSet;
  for (const kind of STRUCTURE_KINDS) out[kind] = markOf(kind);
  return out;
}

/** The kind word of each `ObjKind` the build draws. */
const KIND_WORDS: Readonly<Record<number, MarkKind>> = {
  [ObjKind.OBJ_KIND_THRONE]: 'throne',
  [ObjKind.OBJ_KIND_SPIRE]: 'spire',
  [ObjKind.OBJ_KIND_FORGE]: 'forge',
  [ObjKind.OBJ_KIND_KELP]: 'kelp',
  [ObjKind.OBJ_KIND_BATTERY]: 'battery',
  [ObjKind.OBJ_KIND_DRYDOCK]: 'drydock',
  [ObjKind.OBJ_KIND_ARCHIVE]: 'archive',
  [ObjKind.OBJ_KIND_MARSHAL]: 'marshal',
  [ObjKind.OBJ_KIND_WARD]: 'ward',
  [ObjKind.OBJ_KIND_BREAKWATER]: 'breakwater',
};

/**
 * The kind word of one `ObjKind` number, or `null` for a number the build
 * does not draw.
 *
 * `OBJ_KIND_UNSPECIFIED` answers `null` as well. A site layer built before
 * the first Sieges frame knows no kind, so it keys its control points at the
 * unspecified kind and the caller draws the unkeyed mark of today (R1104).
 */
export function structureKindOf(kind: number): MarkKind | null {
  return KIND_WORDS[kind] ?? null;
}

// ---------------------------------------------------------------------------
// The small path helpers
// ---------------------------------------------------------------------------

/** The water's dark, which a glyph is knocked out of a core in, and the
 *  pale of the palette, `--text` (`ui-map-interface.md` §2): the pennant
 *  mast, the gauge seam and the heraldic argent. The recipe kit owns both. */
export { PALE_INK as PALE, WATER_INK as WATER } from '../recipe-kit';

/** The tilt four sets draw the Deep Battery's tube at, in radians. */
export const TILT = -0.35;

/**
 * The still value of the beacon blink. `blink(t)` of the mockup pages runs
 * from 0.4 to 1, and the atlas bakes one cell, so the light stands at the
 * middle (R1104).
 */
export const BLINK = 0.7;

/** A regular polygon of `n` corners as a point list, the first at `start`. */
export function regular(
  cx: number, cy: number, r: number, n: number, start: number,
): number[] {
  const pts: number[] = [];
  for (let k = 0; k < n; k++) {
    const a = start + (k * Math.PI * 2) / n;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return pts;
}

/** A stroked segment with round ends — `line()` of the mockup page. */
export function line(
  g: MarkGraphics, x0: number, y0: number, x1: number, y1: number,
  color: number, alpha: number, width: number,
): void {
  g.moveTo(x0, y0).lineTo(x1, y1)
    .stroke({ width, color, alpha, join: 'round', cap: 'round' });
}

/** A filled disc — `disc()` of the mockup page. */
export function disc(
  g: MarkGraphics, x: number, y: number, r: number, color: number, alpha: number,
): void {
  g.circle(x, y, r).fill({ color, alpha });
}

/** A stroked ring — `circle()` of the mockup page with no dash. */
export function ring(
  g: MarkGraphics, x: number, y: number, r: number,
  color: number, alpha: number, width: number,
): void {
  g.circle(x, y, r).stroke({ width, color, alpha });
}

/** The stroke of the mockup page: round joins and round caps. */
export function stroke(
  g: MarkGraphics, color: number, alpha = 0.9, width = 1.2,
): void {
  g.stroke({ width, color, alpha, join: 'round', cap: 'round' });
}

/**
 * One dashed ring as one arc per dash.
 *
 * `MarkGraphics` has no dash array, so a dashed ring is stated dash by dash,
 * each with its own `moveTo`, as the path rule of `marks.ts` requires.
 * `fill` is the lit fraction of one dash cell, the same figure the circle
 * batch takes. A `count` of 0 draws a whole ring.
 */
export function dashedRing(
  g: MarkGraphics, r: number, count: number, fill: number,
  color: number, alpha: number, width: number, start = 0,
): void {
  if (count <= 0) {
    ring(g, 0, 0, r, color, alpha, width);
    return;
  }
  const cell = (Math.PI * 2) / count;
  for (let k = 0; k < count; k++) {
    const a0 = start + k * cell;
    g.moveTo(Math.cos(a0) * r, Math.sin(a0) * r);
    g.arc(0, 0, r, a0, a0 + cell * fill);
  }
  g.stroke({ width, color, alpha });
}

/**
 * One arc as its own sub-path.
 *
 * `arc()` from a current point draws a line to the start of the arc, so the
 * caller must `moveTo` the start first. This does both, and it leaves the
 * stroke to the caller.
 */
export function arcPath(
  g: MarkGraphics, cx: number, cy: number, r: number, a0: number, a1: number,
): void {
  g.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
  g.arc(cx, cy, r, a0, a1);
}

/**
 * One elliptical arc, sampled into the point list a `poly` is built from.
 *
 * `MarkGraphics` draws a circular arc alone, so an oval and the bowl of a
 * letter are stated point by point. `out` already holds the start point when
 * the arc continues a path; pass an empty array for an arc of its own. A
 * whole oval is `a0` 0 and `a1` two pi.
 */
export function ellipseArc(
  out: number[], cx: number, cy: number, rx: number, ry: number,
  a0: number, a1: number, steps = 18,
): void {
  for (let i = 0; i <= steps; i++) {
    const a = a0 + (a1 - a0) * (i / steps);
    out.push(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry);
  }
}

/**
 * One rectangle with round corners, sampled into a point list.
 *
 * `MarkGraphics` has no rounded rectangle, and a path of arcs cannot carry a
 * rotation. A point list can, so the plot of a letter frame and the barrel of
 * a buoy both take this. `x` and `y` are the top left corner, the way
 * `roundRect` of a 2D canvas takes them.
 */
export function roundRectPoints(
  x: number, y: number, w: number, h: number, r: number, steps = 4,
): number[] {
  const rad = Math.min(r, w / 2, h / 2);
  const pts: number[] = [];
  const corner = (cx: number, cy: number, a0: number): void => {
    for (let i = 0; i <= steps; i++) {
      const a = a0 + (Math.PI / 2) * (i / steps);
      pts.push(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad);
    }
  };
  corner(x + w - rad, y + rad, -Math.PI / 2);
  corner(x + w - rad, y + h - rad, 0);
  corner(x + rad, y + h - rad, Math.PI / 2);
  corner(x + rad, y + rad, Math.PI);
  return pts;
}

/** One point turned about the origin, for art the mockup drew under a rotation. */
export function rot(x: number, y: number, a: number): [number, number] {
  const c = Math.cos(a), s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
}
